import moment from 'moment'
import type { Moment } from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { SERVICE_TIME_FORMAT } from '@/app/constants/date'
import {
  AggregatedCompanyDetailsModel,
  CompanyDetailsModel,
} from '@/app/models/companyDetailsModel'
import { CreateCompanyModel } from '@/app/models/createCompanyModel'
import { CompanyEditModel } from '@/app/models/companyEditModel'
import { CompanyFilterResult } from 'app/models/company/companyFilter'
import {
  PaymentCompanyModel,
  PaymentCompanyResultModel,
} from '@/app/models/company/paymentCompanyModel'
import { PackageHistoryModel } from '@/app/models/company/packageHistoryModel'
import { CompanyActiveDriverModel } from '@/app/models/company/CompanyActiveDriverModel'
import { CurrentCompanyModel } from '@/app/models/company/currentCompanyModel'
import { CompanyConfigurationModel } from '@/app/models/company/companyConfigurationModel'
import { GusCompanyData } from '@/app/models/company/gusCompanyData'

export interface DatabaseSizeModel {
  databaseId: number
  sizeBytes: number | null
}

export interface CompanyDatabaseSizeBulkModel {
  companyId: string
  databases: DatabaseSizeModel[]
}

const useCompanyService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)

  const getCompanies = async (
    tenantId?: string,
    includeDeleted: boolean = false,
  ): Promise<CompanyDetailsModel[]> => {
    const stopLoading = startLoading()
    try {
      const url =
        tenantId == null
          ? `/config-api/company?&includeDeleted=${includeDeleted}`
          : `/config-api/company/${tenantId}?&ignoreMigration=true`
      const res = await axiosInstance.get<CompanyDetailsModel[]>(url)
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getCompaniesSimple = async (
    tenantId?: string,
    includeDeleted: boolean = false,
  ): Promise<CompanyDetailsModel[]> => {
    const stopLoading = startLoading()
    try {
      const url =
        tenantId == null
          ? `/config-api/company/companiesSimple?&includeDeleted=${includeDeleted}`
          : `/config-api/company/companiesSimple?&ignoreMigration=true&tenantId=${tenantId}`
      const res = await axiosInstance.get<CompanyDetailsModel[]>(url)
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getCompaniesSimpleForGroups = async (
    ids: Array<string>,
    includeDeleted: boolean = false,
  ): Promise<CompanyDetailsModel[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post<CompanyDetailsModel[]>(
        '/config-api/company/companiesForGroups',
        {
          ids: ids,
          includeDeleted: includeDeleted,
        },
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getPaymentCompanies = async (
    source: number,
    exported: boolean,
    notExported: boolean,
  ): Promise<PaymentCompanyResultModel | null> => {
    const stopLoading = startLoading()
    try {
      const url = `/config-api/company/paymentCompanies?&source=${source}&exported=${exported}&notExported=${notExported}`
      const res = await axiosInstance.get<PaymentCompanyResultModel>(url)
      return res.data
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const getPackageHistory = async (
    companyId: string,
  ): Promise<PackageHistoryModel[]> => {
    const stopLoading = startLoading()
    try {
      const url = `/config-api/company/packageHistory?&companyId=${companyId}`
      const res = await axiosInstance.get<PackageHistoryModel[]>(url)
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getFilteredCompanies = async (
    tenantId?: string,
    take?: number,
    skip?: number,
    orderBy?: string,
    descending?: boolean,
    filters?: CompanyFilterResult,
    includeDeleted: boolean = false,
  ): Promise<AggregatedCompanyDetailsModel | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post<AggregatedCompanyDetailsModel>(
        '/config-api/company/listFiltered',
        {
          paged: true,
          includeDeleted: false,
          tenantId: tenantId,
          take: take,
          skip: skip,
          orderBy: orderBy,
          descending: descending,
          filters: filters,
        },
      )
      return res.data
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const getDemoCompanies = async (
    tenantId?: string,
    take?: number,
    skip?: number,
    orderBy?: string,
    descending?: boolean,
    filters?: CompanyFilterResult,
    includeDeleted: boolean = false,
  ): Promise<AggregatedCompanyDetailsModel | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post<AggregatedCompanyDetailsModel>(
        '/config-api/company/demo',
        {
          paged: true,
          includeDeleted: false,
          tenantId: tenantId,
          take: take,
          skip: skip,
          orderBy: orderBy,
          descending: descending,
          filters: filters,
        },
      )
      return res.data
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const getActiveDrivers = async (
    companyId?: string,
  ): Promise<CompanyActiveDriverModel[]> => {
    const stopLoading = startLoading()
    try {
      const url = `/config-api/company/${companyId}/activeDrivers`
      const res = await axiosInstance.get<CompanyActiveDriverModel[]>(url)
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getCurrentCompany = async (
    companyId: string,
  ): Promise<CurrentCompanyModel | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get<CurrentCompanyModel>(
        `api/${companyId}/company`,
      )
      return res.data
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const getWpfCompanies = async (
    companyId: string,
    serverId: string,
  ): Promise<CompanyConfigurationModel[]> => {
    const stopLoading = startLoading()
    try {
      const url =
        companyId != null
          ? `/api/${serverId}/wpfcompany/?&companyId=${companyId}&serverId=${serverId}`
          : `/api/${serverId}/wpfcompany/?&serverId=${serverId}`
      const res = await axiosInstance.get<CompanyConfigurationModel[]>(url)
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const importFromGus = async (nip: string): Promise<GusCompanyData | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get<GusCompanyData>(
        `/config-api/company/importFromGus/?&nip=${nip}`,
      )
      return res.data
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const getCompany = async (
    companyId: string,
    ignoreMigration: boolean,
  ): Promise<CompanyDetailsModel | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get<CompanyDetailsModel>(
        `/config-api/company/${companyId}?&ignoreMigration=${ignoreMigration}`,
      )
      return res.data
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const addCompany = async (
    companyInput: CreateCompanyModel,
  ): Promise<CompanyDetailsModel> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post<CompanyDetailsModel>(
        '/config-api/company/create',
        companyInput,
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const editPaymentCompany = async (
    company: PaymentCompanyModel,
    force: boolean,
  ): Promise<PaymentCompanyModel> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post<PaymentCompanyModel>(
        '/config-api/company/savePaymentCompany',
        {
          Company: {
            ...company,
            suspensionDate:
              company.suspensionDate != null
                ? moment(company.suspensionDate).format(SERVICE_TIME_FORMAT)
                : null,
            suspendedFrom:
              company.suspendedFrom != null
                ? moment(company.suspendedFrom).format(SERVICE_TIME_FORMAT)
                : null,
            suspendedTo:
              company.suspendedTo != null
                ? moment(company.suspendedTo).format(SERVICE_TIME_FORMAT)
                : null,
          },
          force,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const editDemoCompany = async (
    companyId: string,
    dateTo: Moment,
    name: string,
  ): Promise<boolean> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post<boolean>(
        '/config-api/company/editDemoCompany',
        {
          companyId: companyId,
          dateTo: moment(dateTo).utc().format(SERVICE_TIME_FORMAT),
          name: name,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const changeCompany = async (companyId: string) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post('/config-api/company/change', {
        id: companyId,
      })
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const editCompany = async (
    company: CompanyEditModel,
  ): Promise<CompanyDetailsModel> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post<CompanyDetailsModel>(
        '/config-api/company/edit',
        company,
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const deleteCompany = async (ids: string[]) => {
    const stopLoading = startLoading()
    try {
      return await Promise.all(
        ids.map(async (id) => {
          const res = await axiosInstance.post('/config-api/company/delete', {
            id,
          })
          return res.data
        }),
      )
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const uploadFile = async (file: any) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/config-api/company/fileUpload`,
        file,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const resetPaymentState = async (companyId: string) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        '/config-api/company/resetPaymentState',
        { companyId },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const activateCompany = async (companyIds: string[], isActive: boolean) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post('/config-api/company/activate', {
        companyIds: companyIds,
        isActive: isActive,
      })
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const getCompanyDatabaseSizes = async (
    companyId: string,
  ): Promise<DatabaseSizeModel[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get<DatabaseSizeModel[]>(
        `/config-api/companydatabase/sizes?companyId=${companyId}`,
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getCompanyDatabaseSizesBulk = async (
    companyIds: string[],
  ): Promise<CompanyDatabaseSizeBulkModel[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post<CompanyDatabaseSizeBulkModel[]>(
        '/config-api/companydatabase/sizesBulk',
        { companyIds },
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  return {
    getCompanies,
    getCompaniesSimple,
    getCompaniesSimpleForGroups,
    getPaymentCompanies,
    getPackageHistory,
    getFilteredCompanies,
    getDemoCompanies,
    getActiveDrivers,
    getCurrentCompany,
    getWpfCompanies,
    importFromGus,
    getCompany,
    addCompany,
    editPaymentCompany,
    editDemoCompany,
    changeCompany,
    editCompany,
    deleteCompany,
    uploadFile,
    resetPaymentState,
    activateCompany,
    getCompanyDatabaseSizes,
    getCompanyDatabaseSizesBulk,
  }
}

export default useCompanyService
